import React, { useEffect, useLayoutEffect, useState } from 'react';
import styles from './gerador.module.scss';
import type { TourStep } from './tourSteps.ts';

type TourProps = {
    open: boolean;
    stepIndex: number;
    steps: TourStep[];
    onNext: () => void;
    onPrev: () => void;
    onClose: () => void;
};

type Box = { top: number; left: number; width: number; height: number };

export const Tour: React.FC<TourProps> = ({ open, stepIndex, steps, onNext, onPrev, onClose }) => {
    const [box, setBox] = useState<Box | null>(null);
    const step = steps[stepIndex];
    const last = stepIndex === steps.length - 1;

    const updateBox = React.useCallback(() => {
        if (!open || !step) {
            setBox(null);
            return;
        }
        const el = document.querySelector<HTMLElement>(`[data-tour="${step.target}"]`);
        if (!el) {
            setBox(null);
            return;
        }
        el.scrollIntoView({ block: 'center', behavior: 'smooth' });
        const rect = el.getBoundingClientRect();
        setBox({
            top: rect.top,
            left: rect.left,
            width: rect.width,
            height: rect.height,
        });
    }, [open, step]);

    useLayoutEffect(() => {
        updateBox();
    }, [updateBox, stepIndex]);

    useEffect(() => {
        if (!open) return;
        const id = window.setTimeout(updateBox, 280);
        window.addEventListener('resize', updateBox);
        window.addEventListener('scroll', updateBox, true);
        return () => {
            window.clearTimeout(id);
            window.removeEventListener('resize', updateBox);
            window.removeEventListener('scroll', updateBox, true);
        };
    }, [open, updateBox, stepIndex]);

    if (!open || !step) return null;

    const pad = 8;
    const highlight = box
        ? {
            top: Math.max(8, box.top - pad),
            left: Math.max(8, box.left - pad),
            width: box.width + pad * 2,
            height: box.height + pad * 2,
        }
        : null;

    const tooltipStyle = (() => {
        if (!highlight) {
            return { top: '50%', left: '50%', transform: 'translate(-50%, -50%)' } as React.CSSProperties;
        }
        const below = highlight.top + highlight.height + 16;
        const spaceBelow = window.innerHeight - below;
        const top = spaceBelow < 220 ? Math.max(16, highlight.top - 210) : below;
        const left = Math.min(Math.max(16, highlight.left), window.innerWidth - 336);
        return { top, left, transform: 'none' } as React.CSSProperties;
    })();

    return (
        <div className={styles.tourRoot}>
            <div className={styles.tourDim} onClick={onClose} />
            {highlight ? (
                <div
                    className={styles.tourSpot}
                    style={{
                        top: highlight.top,
                        left: highlight.left,
                        width: highlight.width,
                        height: highlight.height,
                    }}
                />
            ) : null}
            <div className={styles.tourCard} style={tooltipStyle} role="dialog" aria-modal="true">
                <p className={styles.tourStepCount}>
                    {stepIndex + 1} de {steps.length}
                </p>
                <h3>{step.title}</h3>
                <p>{step.body}</p>
                <div className={styles.tourActions}>
                    <button type="button" className={styles.ghostBtn} onClick={onClose}>
                        Pular
                    </button>
                    <div>
                        <button type="button" className={styles.ghostBtn} onClick={onPrev} disabled={stepIndex === 0}>
                            Voltar
                        </button>
                        <button type="button" className={styles.primaryBtn} onClick={last ? onClose : onNext}>
                            {last ? 'Concluir' : 'Próximo'}
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};
